import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Fieldset } from "@headlessui/react";
import SlideOver from "../../Modal/SlideOver";
import Button from "../../Button";
import FormError from "../../Form/FormError";
import CollapsibleFormItems from "../../CollapsibleForm";
import {
  ALLOWANCE_TYPE_OPTIONS,
  PAYMENT_FREQUENCY_OPTIONS,
  isBusinessWriteBlocked,
} from "../../../../Constants/business";
import {
  addLocalNotification,
  clearBusinessEmployee,
  requestUpdateEmployeeAllowances,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
} from "../../../../Context";
import WriteBlockGate from "../../Business/WriteBlockGate";
import { cancelAPIRequest } from "../../../../Utils";
import {
  filterBenefitsByMonth,
  getEmptyFormErrors,
  getMonthName,
  isEmptyBenefitItem,
  prepareBenefitsData,
  validateBenefitItem,
} from "../../../../Utils/employeeBenefits";

const MAX_ALLOWANCES = 10;

const fieldConfig = {
  errorKey: "allowanceItems",
  itemName: "allowance",
  nameLabel: "employees.allowances.fields.name",
  namePlaceholder: "employees.allowances.placeholders.name",
  descriptionLabel: "employees.allowances.fields.description",
  descriptionPlaceholder: "employees.allowances.placeholders.description",
  typeLabel: "employees.allowances.fields.type",
  typePlaceholder: "employees.allowances.placeholders.type",
  amountLabel: "employees.allowances.fields.amount",
  amountPlaceholder: "employees.allowances.placeholders.amount",
  frequencyLabel: "employees.allowances.fields.frequency",
  frequencyPlaceholder: "employees.allowances.placeholders.frequency",
  taxableLabel: "employees.allowances.fields.taxable",
  startDateLabel: "employees.allowances.fields.startDate",
  startDatePlaceholder: "employees.allowances.placeholders.startDate",
  endDateLabel: "employees.allowances.fields.endDate",
  endDatePlaceholder: "employees.allowances.placeholders.endDate",
  statutoryAmountBadge: "employees.allowances.statutoryAmountBadge",
  statutoryAmountHint: "employees.allowances.statutoryAmountHint",
};

const AllowanceModal = ({
  open,
  onClose,
  employee,
  selectedMonth,
  selectedYear,
}) => {
  // Benefits are filtered against the payroll period being run, not today.
  const period = selectedMonth
    ? { year: selectedYear || new Date().getFullYear(), month: selectedMonth }
    : null;

  // Hooks
  const { t } = useTranslation();

  // Context and reducer related
  const dispatch = useBusinessTransactionDispatch();
  const {
    request,
    updateAllowancesLoading,
    updateAllowancesError,
    updatedAllowances,
  } = useBusinessTransactionState();
  const notificationDispatch = useNotificationDispatch();
  const { user } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);

  // State
  const [allowanceItems, setAllowanceItems] = useState([]);
  const [errors, setErrors] = useState({});

  // Constants
  const isEdit = Boolean(
    employee?.id && employee?.allowances?.items?.length > 0
  );

  // useEffect hooks
  useEffect(() => {
    return () => {
      if (request) {
        cancelAPIRequest(request);
      }
      clearBusinessEmployee(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (isEdit) {
      const mappedAllowances = employee.allowances.items.map((allowance) => ({
        name: allowance.name || "",
        description: allowance.description || "",
        amount: allowance.amount || 0,
        type:
          ALLOWANCE_TYPE_OPTIONS.find((opt) => opt.value === allowance.type) ||
          null,
        frequency:
          PAYMENT_FREQUENCY_OPTIONS.find(
            (opt) => opt.value === allowance.frequency
          ) || null,
        isTaxable: allowance.isTaxable,
        startDate: allowance.startDate ? new Date(allowance.startDate) : null,
        endDate: allowance.endDate ? new Date(allowance.endDate) : null,
      }));

      // Apply month filtering if selectedMonth is provided
      const filteredAllowances = selectedMonth
        ? filterBenefitsByMonth(mappedAllowances, period)
        : mappedAllowances;

      setAllowanceItems(filteredAllowances);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, isEdit, selectedMonth]);

  useEffect(() => {
    if (updatedAllowances?.employeeID) {
      // Close modal
      onClose();

      // Reset form data
      setAllowanceItems([]);

      // Clear business employee update
      clearBusinessEmployee(dispatch);

      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `update-allowance-${updatedAllowances?.employeeID}`,
        type: "success",
        title: "common.success",
        body: isEdit
          ? "employees.notifications.allowanceUpdated"
          : "employees.notifications.allowanceCreated",
        showFull: true,
      });
    }
  }, [
    updatedAllowances?.employeeID,
    dispatch,
    onClose,
    isEdit,
    notificationDispatch,
  ]);

  const monthName = getMonthName(period);
  const isEmptyItem = isEmptyBenefitItem;
  const validateItem = (item) => validateBenefitItem(item, "allowance");
  const emptyErrors = getEmptyFormErrors("allowance");

  const validateForm = () => {
    const allItemsEmpty = allowanceItems.every(isEmptyItem);

    // Edit mode with empty items is valid
    if (isEdit && allItemsEmpty) return true;

    const errors = {};

    if (!isEdit && allItemsEmpty) {
      Object.assign(errors, emptyErrors);
    } else {
      const itemErrors = allowanceItems.map(validateItem);
      if (itemErrors.some((error) => error !== null)) {
        errors.allowanceItems = itemErrors;
      }
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isEdit && allowanceItems.length === 0) return;

    if (!validateForm()) return;
    const edited_allowances = selectedMonth
      ? prepareBenefitsData(
          allowanceItems,
          "allowance",
          period,
          employee?.allowances?.items
        )
      : prepareBenefitsData(allowanceItems, "allowance");

    requestUpdateEmployeeAllowances(dispatch, {
      employee_id: employee.id,
      allowances: edited_allowances,
    });
  };

  const handleClose = () => {
    onClose();
    setErrors({});
    setAllowanceItems([]);
    clearBusinessEmployee(dispatch);
  };

  return (
    <SlideOver
      open={open}
      onClose={handleClose}
      title={
        selectedMonth
          ? !isEdit
            ? `${t("employees.modal.title.allowance")} - ${monthName}`
            : `${t("employees.modal.title.editAllowance")} - ${monthName}`
          : !isEdit
          ? "employees.modal.title.allowance"
          : "employees.modal.title.editAllowance"
      }
      footer={
        <div className="flex justify-between items-center">
          <Button type="button" onClick={onClose} variant="secondary">
            {t("common.back")}
          </Button>
          <Button
            loading={updateAllowancesLoading}
            disabled={writeBlocked || updateAllowancesLoading}
            type="submit"
            form="allowance-form"
            variant="primary"
          >
            {t("common.save")}
          </Button>
        </div>
      }
    >
      <form
        id="allowance-form"
        className="space-y-4 mb-6"
        onSubmit={handleSubmit}
      >
        <WriteBlockGate>
        <FormError error={updateAllowancesError} />
        <Fieldset
          disabled={updateAllowancesLoading}
          className="space-y-4 data-[disabled]:opacity-50"
        >
          <CollapsibleFormItems
            items={allowanceItems}
            setItems={setAllowanceItems}
            typeOptions={ALLOWANCE_TYPE_OPTIONS}
            errors={errors}
            setErrors={setErrors}
            maxItems={MAX_ALLOWANCES}
            fieldConfig={fieldConfig}
            defaultStartDate={employee?.startDate}
            showTaxableField
            employee={employee}
            selectedMonth={selectedMonth}
            titleKey="employees.allowances.label"
            addButtonKey="employees.allowances.footer.addAllowance"
            maxReachedKey="employees.allowances.footer.maxAllowancesReached"
            maxMessageKey="employees.allowances.footer.maxAllowancesMessage"
            initiallyCollapsed={true}
            // JKK/JKM are the employer's premiums, derived every month in the
            // Salary Table, so their amount is shown and never typed.
            lockStatutoryAmounts
          />
        </Fieldset>
        </WriteBlockGate>
      </form>
    </SlideOver>
  );
};

export default AllowanceModal;
